import React from "react";
import { Layers } from "lucide-react";
import useAccess from "../hooks/useAccess.jsx";
import { ACCESS_PERMISSION_KEYS } from "../lib/accessControl.js";
import { fetchSystemConfigState, updateSystemConfigState } from "../lib/systemConfigApi.js";
import { HoverCard, useToast } from "./ResultsPrimitives.jsx";
import DispatchHeaderMenuSwitch from "./resultsPageChrome/DispatchHeaderMenuSwitch.jsx";

export const SAME_OD_GROUPING_SETTING_KEY = "same_od_grouping_indivisible_enabled";
const DEFAULT_VALUE = false;

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function resolveBooleanValue(value) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value !== "string") {
    return null;
  }

  const normalizedValue = value.trim().toLowerCase();
  if (normalizedValue === "true") {
    return true;
  }
  if (normalizedValue === "false") {
    return false;
  }

  return null;
}

export function resolveSystemConfigState(payload) {
  if (isRecord(payload?.state)) {
    return payload.state;
  }

  return isRecord(payload) ? payload : null;
}

export function resolveSameOdGroupingSetting(payload) {
  const state = resolveSystemConfigState(payload);
  const setting = Array.isArray(state?.settings)
    ? state.settings.find((candidate) => candidate?.key === SAME_OD_GROUPING_SETTING_KEY) || null
    : null;
  const value = resolveBooleanValue(setting?.value);

  return {
    canEdit: Boolean(setting)
      && setting.can_edit !== false
      && state?.can_edit !== false,
    setting,
    state,
    value,
  };
}

function resolveToggleErrorMessage(
  error,
  fallbackMessage = "Impossible de charger le réglage Groupes inséparables.",
) {
  return error instanceof Error && error.message.trim()
    ? error.message.trim()
    : fallbackMessage;
}

function SameOdGroupingHoverCardContent() {
  return (
    <div className="grid gap-2">
      <p className="m-0">
        <strong>Activé :</strong> le regroupement de commandes reste entier. S’il ne tient pas sur un seul camion, le groupe est placé dans « Non assignables » plutôt que réparti sur plusieurs camions.
      </p>
      <p className="m-0">
        <strong>Désactivé :</strong> si le regroupement ne tient pas sur un seul camion, il peut être réparti sur plusieurs camions, avec le nombre minimal de camions nécessaire.
      </p>
      <p className="m-0 text-[11px] text-floating-faint">
        Le réglage s’applique à la prochaine optimisation.
      </p>
    </div>
  );
}

export default function SameOdGroupingToggle({
  isMenuOpen = true,
  isOptimizationInProgress = false,
  onMutationStateChange = null,
} = {}) {
  const { hasPermission, isReady: isAccessReady } = useAccess();
  const { showToast } = useToast();
  const [enabled, setEnabled] = React.useState(DEFAULT_VALUE);
  const [settingCanEdit, setSettingCanEdit] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const confirmedValueRef = React.useRef(DEFAULT_VALUE);
  const requestSequenceRef = React.useRef(0);
  const descriptionId = React.useId();
  const canWritePermission = Boolean(
    isAccessReady
      && hasPermission(ACCESS_PERMISSION_KEYS.systemSettingsSolverSameOdGroupingWrite),
  );
  const canReadSystemConfig = Boolean(
    isAccessReady
      && (canWritePermission || hasPermission(ACCESS_PERMISSION_KEYS.systemSettingsRead)),
  );

  React.useEffect(() => {
    let isCancelled = false;
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;

    if (!isMenuOpen || isUpdating) {
      return () => { isCancelled = true; };
    }

    if (!isAccessReady) {
      setIsLoading(true);
      return () => {
        isCancelled = true;
      };
    }

    if (!canReadSystemConfig) {
      setIsLoading(false);
      setSettingCanEdit(false);
      setEnabled(DEFAULT_VALUE);
      confirmedValueRef.current = DEFAULT_VALUE;
      return () => {
        isCancelled = true;
      };
    }

    setIsLoading(true);
    void fetchSystemConfigState()
      .then((payload) => {
        if (isCancelled || requestSequenceRef.current !== requestSequence) {
          return;
        }

        const resolvedSetting = resolveSameOdGroupingSetting(payload);
        if (resolvedSetting.value === null) {
          throw new Error("La configuration Groupes inséparables est absente ou invalide.");
        }

        confirmedValueRef.current = resolvedSetting.value;
        setEnabled(resolvedSetting.value);
        setSettingCanEdit(canWritePermission && resolvedSetting.canEdit);
      })
      .catch((error) => {
        if (isCancelled || requestSequenceRef.current !== requestSequence) {
          return;
        }

        setEnabled(DEFAULT_VALUE);
        confirmedValueRef.current = DEFAULT_VALUE;
        setSettingCanEdit(false);
        if (canWritePermission) {
          showToast({
            duration: 7000,
            title: "Groupes inséparables indisponibles",
            message: `${resolveToggleErrorMessage(error)} Le comportement par défaut (désactivé) est conservé.`,
            type: "error",
          });
        }
      })
      .finally(() => {
        if (!isCancelled && requestSequenceRef.current === requestSequence) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [canReadSystemConfig, canWritePermission, isAccessReady, isMenuOpen, isUpdating, showToast]);

  const handleChange = React.useCallback(async (nextValue) => {
    if (!canWritePermission || !settingCanEdit || isLoading || isUpdating || isOptimizationInProgress) {
      return;
    }

    const previousValue = confirmedValueRef.current;
    setEnabled(nextValue);
    setIsUpdating(true);
    onMutationStateChange?.(true);

    try {
      const payload = await updateSystemConfigState({
        settings: [{
          key: SAME_OD_GROUPING_SETTING_KEY,
          value: nextValue,
        }],
      });
      const resolvedSetting = resolveSameOdGroupingSetting(payload);
      if (resolvedSetting.value === null) {
        throw new Error("La réponse du serveur pour Groupes inséparables est incomplète.");
      }

      confirmedValueRef.current = resolvedSetting.value;
      setEnabled(resolvedSetting.value);
      setSettingCanEdit(resolvedSetting.canEdit && canWritePermission);
      showToast({
        duration: 6000,
        title: "Groupes inséparables enregistrés",
        message: resolvedSetting.value
          ? "Les groupes chargement/destination/produit resteront sur un seul camion à la prochaine optimisation."
          : "Un groupe restera sur un seul camion lorsqu’il y entre physiquement; sinon le moteur utilisera le nombre minimal de camions à la prochaine optimisation.",
        type: "success",
      });
    } catch (error) {
      confirmedValueRef.current = previousValue;
      setEnabled(previousValue);
      showToast({
        duration: 7000,
        title: "Groupes inséparables non enregistrés",
        message: resolveToggleErrorMessage(
          error,
          "Impossible d’enregistrer le réglage Groupes inséparables.",
        ),
        type: "error",
      });
    } finally {
      setIsUpdating(false);
      onMutationStateChange?.(false);
    }
  }, [canWritePermission, isLoading, isOptimizationInProgress, isUpdating, onMutationStateChange, settingCanEdit, showToast]);

  const isDisabled = !canWritePermission || !settingCanEdit || isLoading || isUpdating || isOptimizationInProgress;
  return (
    <HoverCard
      title="Groupes inséparables"
      content={<SameOdGroupingHoverCardContent />}
      placement="bottom-end"
      delay={140}
      maxWidth={420}
      referenceAsChild
    >
      <button
        type="button"
        role="menuitemcheckbox"
        aria-label="Groupes inséparables"
        aria-checked={enabled}
        aria-describedby={descriptionId}
        aria-busy={isLoading || isUpdating}
        className="dispatch-header-menu-action"
        disabled={isDisabled}
        onClick={() => handleChange(!enabled)}
        tabIndex={-1}
        data-keep-menu-open="true"
        data-same-od-grouping-toggle="true"
        data-same-od-grouping-indivisible-toggle="true"
      >
        <Layers aria-hidden="true" className="h-4 w-4 shrink-0" strokeWidth={2.15} />
        <span className="min-w-0 flex-1">
          Groupes inséparables
        </span>
        <DispatchHeaderMenuSwitch checked={enabled} isBusy={isUpdating} />
        <span id={descriptionId} className="sr-only">
          Activé : toutes les commandes d’un même groupe chargement, destination et produit restent ensemble ou sont placées dans « Non assignables » si elles ne tiennent pas sur un seul camion. Désactivé : le groupe peut être réparti sur plusieurs camions si nécessaire, avec le nombre minimal de camions. Le changement s’applique à la prochaine optimisation.
        </span>
      </button>
    </HoverCard>
  );
}
